import React, { useState, useEffect } from 'react';
import { LoginForm } from './LoginForm';
import { RegisterForm } from './RegisterForm';
import { LoginWithProvider } from './LoginWithProvider';
import { Trophy, UserPlus } from 'lucide-react';
import { useLanguageStore } from '../../lib/languageStore';
import { translate } from '../../lib/translations';
import { useUserStore } from '../../lib/userStore';
import { getEmbedAwarePath, useEmbedStore } from '../../lib/embedStore';
import { isArsenalMode } from '../../lib/config';
import { isTslhTenant } from '../../lib/tenantStore';
import { joinTslhLeagueAndRedirect } from '../../lib/tenantJoin';
import { useNavigate } from 'react-router-dom';

export function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const { language } = useLanguageStore();
  const username = useUserStore((state) => state.username);
  const userId = useUserStore((state) => state.user_id);
  const isBpmSession = useUserStore((state) => state.is_bpm_session);
  const isTslhSession = useUserStore((state) => state.is_tslh_session);
  const isEmbed = useEmbedStore((state) => state.isEmbed);
  const navigate = useNavigate();

  useEffect(() => {
    // Si l'utilisateur est déjà connecté :
    // - Tenant TSLH : garantir la ligue 152 puis rediriger vers /build-my-team ou /my-office
    // - Sinon : rediriger vers /my-office (ou /standings en mode embed classique)
    if (!username) return;
    if (isTslhTenant() && userId) {
      // Idempotent : joinTslhLeagueAndRedirect vérifie déjà la membership
      // et navigate() vers la page appropriée. Ne relance pas si la session
      // TSLH est déjà marquée ET qu'on est déjà sur une page TSLH.
      if (!isTslhSession) {
        joinTslhLeagueAndRedirect(userId, navigate);
        return;
      }
    }
    navigate(isEmbed ? '/standings' : '/my-office');
  }, [username, userId, isEmbed, isTslhSession, navigate]);

  useEffect(() => {
    // Session BPM/embed expirée sans reconnexion (ex: reset après une longue absence) :
    // relance automatiquement le login OIDC plutôt que d'afficher le formulaire classique.
    // Exclusion : tenant TSLH utilise l'auth interne, jamais OIDC.
    if (!username && (isBpmSession || isEmbed) && isArsenalMode() && !isTslhTenant()) {
      navigate(getEmbedAwarePath('/auth/bpm'), { replace: true });
    }
  }, [username, isBpmSession, isEmbed, navigate]);

  return (
    <div className="card shadow-lg border-0">
      <div className="card-body p-5">
        <div className="text-center mb-4">
          {isLogin ? (
            <Trophy className="h-12 w-12 text-primary mb-2" />
          ) : (
            <UserPlus className="h-12 w-12 text-primary mb-2" />
          )}
          <h2 className="card-title text-center mb-4">
            {isLogin
              ? translate('welcome.back', language)
              : isTslhTenant()
                ? (language === 'fr' ? 'Rejoindre le pool TSLH' : 'Join the TSLH pool')
                : translate('join.pool', language)}
          </h2>
        </div>

        {isLogin ? <LoginForm /> : <RegisterForm />}

        <hr className="my-4" />

        {/* Login with partner hidden */}

        <button
          className="btn btn-outline-primary w-100"
          onClick={() => setIsLogin(!isLogin)}
        >
          {isLogin ? translate('need.account', language) : translate('have.account', language)}
        </button>
      </div>
    </div>
  );
}
